import { useFirestore } from "reactfire";
import { TextContext, useText } from "../context/TextContext";
import { Offer, OfferBlock, OfferBlockHeading, OfferBlockSpacer, OfferBlockText, OfferBlockAttachments } from "../models/shared/Offer";
import { useOrganization } from "../context/OrganizationContext";
import { useNavigate } from "react-router-dom";
import { COLLECTIONS, PATHS } from "../utils/shared/constants";
import { Deal } from "../models/shared/Deal";
import { toast } from "react-toastify";
import { Organization } from "../models/shared/Organization";
import { query, collection, where, getDocs, addDoc, updateDoc, doc, deleteField } from "firebase/firestore";
import { Article } from "../models/shared/Article";
import { fromFirebaseDocs } from "../utils/shared/firebase";
import { sortArticles } from "../utils/shared/article";
import { v4 as uuid } from 'uuid';



const createDefaultOfferBlocks = (organization: Organization, text: TextContext): OfferBlock[] => {
    return [
        {
            type: 'orgCustomer',
        },
        {
            type: 'spacer',
            size: 'large',
        } as OfferBlockSpacer,
        {
            type: 'heading',
            content: organization.name || text.organization,
        } as OfferBlockHeading,
        {
            type: 'text',
            content: text.treeServiceDescription,
            fontSize: 16,
        } as OfferBlockText,
        {
            type: 'spacer',
            size: 'medium',
        } as OfferBlockSpacer,
        {
            type: 'heading',
            content: text.address,
        } as OfferBlockHeading,
        {
            type: 'map',
        },
        {
            type: 'spacer',
            size: 'medium',
        } as OfferBlockSpacer,
        {
            type: 'heading',
            content: text.attachments,
        } as OfferBlockHeading,
        {
            type: 'attachments',
        },
        {
            type: 'spacer',
            size: 'medium',
        } as OfferBlockSpacer,
        {
            type: 'lineItems',
        },
        {
            type: 'spacer',
            size: 'medium',
        } as OfferBlockSpacer,
        {
            color: '#676565',
            textAlign: 'center',
            type: 'text',
            content: text.treeServiceThankYou,
        } as OfferBlockText,

    ];
}

export const useEnsureOffer = () => {
    const text = useText();
    const firestore = useFirestore();
    const { orgId, organization } = useOrganization();
    const navigate = useNavigate();

    return async (deal: Deal, offers: Offer[]) => {
        const hasOffers = offers && offers.length > 0
        const offersPath = `${PATHS.DEALS}/${deal.id}${PATHS.OFFERS}`
        if (hasOffers) {
            navigate(offersPath);
            return;
        }

        try {
            // Make sure the deal has an ID
            if (!deal.id) {
                toast.error(text.defaultError);
                return;
            }

            // Get all image attachments from the deal to preselect them
            const imageAttachments = (deal.attachments || [])
                .filter(attachment => attachment.fileType?.startsWith('image/'))
                .map(attachment => attachment.path);

            // Create default blocks with preselected image attachments
            const defaultBlocks = organization.defaultOfferBlocks || createDefaultOfferBlocks(organization, text);
            const blocksWithPreselectedImages = defaultBlocks.map(block => {
                if (block.type === 'attachments') {
                    return {
                        ...block,
                        selectedAttachments: imageAttachments
                    } as OfferBlockAttachments;
                }
                return block;
            });

            const newOffer: Offer = {
                status: 'active',
                createdAt: new Date(),
                updatedAt: new Date(),
                blocks: blocksWithPreselectedImages,
            };
            const distance = deal.placeDistance?.distanceMeters;
            if (distance) {
                const distanceKm = Math.round(distance / 1000)
                const distanceBasedArticlesQuery = query(
                    collection(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`),
                    where('isDistanceBased', '==', true)
                );
                const distanceBasedArticlesSnapshot = await getDocs(distanceBasedArticlesQuery);
                const activeDistanceBasedArticles = sortArticles(fromFirebaseDocs<Article>(distanceBasedArticlesSnapshot.docs))
                    .filter(article => article.status !== 'archived');

                if (activeDistanceBasedArticles.length > 0) {
                    newOffer.lineItems = activeDistanceBasedArticles.map(article => ({
                        id: uuid(),
                        articleId: article.id,
                        description: article.name,
                        unitPrice: article.perUnitPrice,
                        vatRate: article.vatRate,
                        quantity: distanceKm,
                    }));
                }

            }

            const offersCollectionPath = `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${deal.id}/${COLLECTIONS.OFFERS}`;
            await addDoc(collection(firestore, offersCollectionPath), newOffer);
            await updateDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${deal.id}`), {
                updatedAt: new Date(),
                brandNew: deleteField()
            });

            toast.success(text.offerCreated);

            navigate(offersPath);
        } catch (error) {
            console.error('Error creating offer:', error);
            toast.error(text.defaultError);
        }
    };
}
